import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

export type DentistSessionFormRow = {
  dentistName: string;
  day: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  doctorId?: string;
  roomId?: string;
};

type GeneralSession = {
  sessionName: string;
  startTime: string;
  endTime: string;
};

type RoomOption = {
  _id: string;
  roomNumber: string;
  roomName: string;
  doctorsCount: number;
};

type DentistOption = { _id: string; name: string };

type NewRowState = {
  dentistName: string;
  doctorId: string;
  day: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  roomId: string;
};

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function ensureDrPrefix(name: string): string {
  const n = String(name ?? "").trim();
  if (!n) return "";
  if (/^dr\.?\s+/i.test(n)) return n.replace(/^dr\.?\s+/i, "Dr. ");
  return `Dr. ${n}`;
}

function buildHalfHourTimeOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

const HALF_HOUR_OPTIONS = buildHalfHourTimeOptions();

function timeToMinutes(t: string): number {
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function flexibleTimeToMinutes(raw: string): number {
  const str = String(raw ?? "").trim();
  if (!str) return NaN;
  const hhmm = str.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);

  const mer = str.toLowerCase().match(/\b(am|pm)\b/);
  const isPm = mer?.[1] === "pm";
  const isAm = mer?.[1] === "am";
  const cleaned = str.toLowerCase().replace(/\b(am|pm)\b/gi, "").trim();
  const m = cleaned.match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return NaN;
  let h = parseInt(m[1], 10);
  let min = parseInt(m[2] ?? "0", 10);
  if (Number.isNaN(h)) return NaN;
  if (Number.isNaN(min)) min = 0;
  min = Math.min(59, Math.max(0, min));
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  return h * 60 + min;
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return aStart < bEnd && bStart < aEnd;
}

function SessionCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  required,
  disabled,
  onSelect,
}: {
  id: string;
  labelId: string;
  value: string;
  options: GeneralSession[];
  loading: boolean;
  required?: boolean;
  disabled?: boolean;
  onSelect: (s: GeneralSession) => void;
}) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((s) => s.sessionName === value) ?? null,
    [options, value]
  );

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40",
          disabled ? "opacity-60 cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        <div className="min-w-0 flex items-center justify-between gap-2 w-full">
          <span
            className={
              value ? "truncate text-slate-800" : "truncate text-slate-400"
            }
          >
            {loading
              ? "Loading…"
              : value ||
                (options.length === 0 ? "No sessions" : "Select session")}
          </span>
          {!loading && selected?.startTime && selected?.endTime ? (
            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
              {selected.startTime}
              <span className="mx-0.5 font-normal text-slate-400">·</span>
              {selected.endTime}
            </span>
          ) : null}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open && !disabled ? (
        <div
          className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto"
          role="listbox"
          tabIndex={-1}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No sessions found.
            </div>
          ) : (
            options.map((s) => (
              <button
                type="button"
                key={s.sessionName}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(s);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-800">
                    {s.sessionName}
                  </div>
                  {s.startTime && s.endTime ? (
                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
                      {s.startTime}
                      <span className="mx-0.5 font-normal text-slate-400">·</span>
                      {s.endTime}
                    </span>
                  ) : null}
                </div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function RoomCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  required,
  optional,
  disabled,
  getStatus,
  onSelect,
  onClear,
}: {
  id: string;
  labelId: string;
  value: string;
  options: RoomOption[];
  loading: boolean;
  required?: boolean;
  /** When true, show "Assign later" and allow saving without a room. */
  optional?: boolean;
  disabled?: boolean;
  getStatus: (roomNumber: string) => {
    capacity: number;
    used: number;
    available: number;
    isFull: boolean;
    bookedDentists: string[];
  } | null;
  onSelect: (r: RoomOption) => void;
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isUnassigned = optional && !String(value ?? "").trim();
  const selected = useMemo(
    () => options.find((r) => r.roomNumber === value) ?? null,
    [options, value]
  );
  const selectedStatus = useMemo(
    () => (selected ? getStatus(selected.roomNumber) : null),
    [getStatus, selected]
  );

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40",
          disabled ? "opacity-60 cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        <div className="min-w-0 flex items-center justify-between gap-2 w-full">
          <span className={value || isUnassigned ? "truncate text-slate-800" : "truncate text-slate-400"}>
            {loading
              ? "Loading…"
              : isUnassigned
                ? "Assign later (no room)"
                : value || (options.length === 0 ? "No rooms" : "Select room")}
          </span>
          {!loading && selected ? (
            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
              {selectedStatus
                ? `${selectedStatus.available}/${selectedStatus.capacity} free`
                : `${selected.doctorsCount} doctors`}
            </span>
          ) : null}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required && !optional ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open && !disabled ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              {optional ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onClear?.();
                    setOpen(false);
                  }}
                  className={[
                    "w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100",
                    isUnassigned ? "bg-primary/5 font-medium text-primary" : "text-slate-700",
                  ].join(" ")}
                >
                  Assign later (no room)
                </button>
              ) : null}
              {options.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">No rooms found.</div>
              ) : (
                options.map((r) => {
              const st = getStatus(r.roomNumber);
              const roomFull = Boolean(st?.isFull);
              return (
                <button
                  type="button"
                  key={r.roomNumber}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (roomFull) {
                      toast.error("Room is full for selected time.");
                      return;
                    }
                    onSelect(r);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${
                    roomFull ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">
                        Room {r.roomNumber}
                      </div>
                      {r.roomName ? (
                        <div className="text-xs text-slate-500 truncate">
                          {r.roomName}
                        </div>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-xs text-slate-600 tabular-nums">
                      {st ? (
                        st.isFull ? (
                          <span className="font-semibold text-red-600">Full</span>
                        ) : (
                          `${st.available}/${st.capacity} free`
                        )
                      ) : (
                        `${r.doctorsCount} doctors`
                      )}
                    </div>
                  </div>
                </button>
              );
                })
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function DentistCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  onSelect,
  required,
  disabled,
}: {
  id: string;
  labelId: string;
  value: string;
  options: DentistOption[];
  loading: boolean;
  onSelect: (d: DentistOption) => void;
  required?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40",
          disabled ? "opacity-60 cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>
          {loading
            ? "Loading…"
            : value || (options.length === 0 ? "No dentists" : "Select dentist")}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open && !disabled ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No dentists found.
            </div>
          ) : (
            options.map((d) => (
              <button
                type="button"
                key={d._id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(d);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
              >
                {d.name}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function SimpleCombobox({
  id,
  labelId,
  value,
  options,
  placeholder,
  required,
  disabled,
  onSelect,
}: {
  id: string;
  labelId: string;
  value: string;
  options: string[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  onSelect: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40",
          disabled ? "opacity-60 cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>
          {value || placeholder || "Select…"}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open && !disabled ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No results.
            </div>
          ) : (
            options.map((opt) => (
              <button
                type="button"
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
              >
                {opt}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export type AddDentistSessionModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  editSource?: DentistSessionFormRow | null;
  existingSessions: DentistSessionFormRow[];
  /** When set, day is locked to this value (e.g. today on room allocation). */
  fixedDay?: string;
  /** Optional pre-fill when opening create mode. */
  initialValues?: Partial<NewRowState>;
};

export function AddDentistSessionModal({
  open,
  onClose,
  onSaved,
  editSource = null,
  existingSessions,
  fixedDay,
  initialValues,
}: AddDentistSessionModalProps) {
  const [dentistOptions, setDentistOptions] = useState<DentistOption[]>([]);
  const [dentistsLoading, setDentistsLoading] = useState(false);
  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [generalSessions, setGeneralSessions] = useState<GeneralSession[]>([]);
  const [sessionNamesLoading, setSessionNamesLoading] = useState(false);
  const [roomCapacityError, setRoomCapacityError] = useState<string | null>(null);
  const [newRow, setNewRow] = useState<NewRowState>({
    dentistName: "",
    doctorId: "",
    day: fixedDay || weekDays[0],
    sessionName: "",
    startTime: "",
    endTime: "",
    roomNo: "",
    roomId: "",
  });
  const initialValuesRef = useRef(initialValues);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      initialValuesRef.current = initialValues;
    }
    wasOpenRef.current = open;
  }, [open, initialValues]);

  const sessionNameOptions = useMemo(() => generalSessions, [generalSessions]);

  const sessionNameOptionsForModal = useMemo(() => {
    const base = sessionNameOptions;
    const es = editSource;
    if (!es?.sessionName?.trim()) return base;
    const name = es.sessionName.trim();
    if (base.some((s) => s.sessionName === name)) return base;
    return [
      ...base,
      {
        sessionName: name,
        startTime: es.startTime,
        endTime: es.endTime,
      },
    ];
  }, [sessionNameOptions, editSource]);

  const resetNewRow = useCallback(
    (dentists: DentistOption[], roomList: RoomOption[], sessions: GeneralSession[]) => {
      const iv = initialValuesRef.current;
      const firstSession = sessions[0] ?? null;
      const firstDentist = dentists[0] ?? null;
      const hasPrefillRoom = Boolean(String(iv?.roomId ?? "").trim());
      const firstRoom = hasPrefillRoom
        ? roomList.find((r) => r._id === String(iv?.roomId).trim()) ?? roomList[0] ?? null
        : null;
      const dayValue = fixedDay || iv?.day || weekDays[0];

      setNewRow({
        dentistName: iv?.dentistName ?? firstDentist?.name ?? "",
        doctorId: iv?.doctorId ?? firstDentist?._id ?? "",
        day: dayValue,
        sessionName: iv?.sessionName ?? firstSession?.sessionName ?? "",
        startTime: iv?.startTime ?? firstSession?.startTime ?? "",
        endTime: iv?.endTime ?? firstSession?.endTime ?? "",
        roomNo: iv?.roomNo ?? firstRoom?.roomNumber ?? "",
        roomId: iv?.roomId ?? firstRoom?._id ?? "",
      });
    },
    [fixedDay]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDentistsLoading(true);
    baseUrl
      .get("/client/setting/dentistGeneralHours/getDentists")
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.dentists;
        const list: DentistOption[] = Array.isArray(raw)
          ? raw
              .map((d: unknown) => {
                const o = d as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  name: ensureDrPrefix(String(o?.name ?? "")),
                };
              })
              .filter((d) => d._id && d.name)
          : [];
        setDentistOptions(list);
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setDentistOptions([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load dentists"
        );
      })
      .finally(() => {
        if (!cancelled) setDentistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRoomsLoading(true);
    baseUrl
      .get("/client/setting/clinicRoom/getRooms")
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data;
        const list: RoomOption[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  roomNumber: String(o?.roomNumber ?? "").trim(),
                  roomName: String(o?.roomName ?? "").trim(),
                  doctorsCount: Number.isFinite(Number(o?.doctorsCount))
                    ? Number(o?.doctorsCount)
                    : 0,
                };
              })
              .filter((r) => r._id && r.roomNumber)
          : [];
        setRooms(list);
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setRooms([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load rooms"
        );
      })
      .finally(() => {
        if (!cancelled) setRoomsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Initialize form only when the modal opens / edit target changes.
  // Do NOT depend on generalSessions — that reload on day change was resetting day to Monday.
  useEffect(() => {
    if (!open) return;
    if (editSource) {
      const doctorId = String(editSource.doctorId ?? "").trim();
      setNewRow({
        dentistName: editSource.dentistName,
        doctorId,
        day: editSource.day || fixedDay || weekDays[0],
        sessionName: editSource.sessionName,
        startTime: editSource.startTime,
        endTime: editSource.endTime,
        roomNo: editSource.roomNo,
        roomId: String(editSource.roomId ?? "").trim(),
      });
      return;
    }
    resetNewRow(dentistOptions, rooms, generalSessions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit dentistOptions/rooms/generalSessions
  }, [open, editSource, fixedDay, resetNewRow]);

  // Fill dentist default once options arrive, without overwriting the selected day.
  useEffect(() => {
    if (!open || editSource) return;
    setNewRow((p) => {
      if (p.doctorId || dentistOptions.length === 0) return p;
      const first = dentistOptions[0];
      return {
        ...p,
        dentistName: first.name,
        doctorId: first._id,
      };
    });
  }, [open, editSource, dentistOptions]);

  useEffect(() => {
    if (!open) return;
    const day = String(newRow.day ?? "").trim() || fixedDay || weekDays[0];
    let cancelled = false;
    setSessionNamesLoading(true);
    baseUrl
      .get("/client/setting/dentistGeneralHours/getGeneralSessionNames", {
        params: { day },
      })
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.sessions;
        const list: GeneralSession[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  sessionName: String(o?.sessionName ?? "").trim(),
                  startTime: String(o?.startTime ?? "").trim(),
                  endTime: String(o?.endTime ?? "").trim(),
                };
              })
              .filter((s) => s.sessionName)
          : [];
        setGeneralSessions(list);
        if (!editSource) {
          setNewRow((p) => {
            const stillValid =
              !!p.sessionName &&
              list.some((s) => s.sessionName === p.sessionName);
            if (stillValid) return p;
            const first = list[0];
            if (!first) return { ...p, sessionName: "", startTime: "", endTime: "" };
            return {
              ...p,
              sessionName: first.sessionName,
              startTime: first.startTime ?? "",
              endTime: first.endTime ?? "",
            };
          });
        }
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setGeneralSessions([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load session names"
        );
      })
      .finally(() => {
        if (!cancelled) setSessionNamesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, newRow.day, fixedDay, editSource]);

  const currentRoomStatus = useMemo(() => {
    const dayVal = String(newRow.day ?? "").trim();
    const startM = flexibleTimeToMinutes(newRow.startTime);
    const endM = flexibleTimeToMinutes(newRow.endTime);
    const canCompute =
      dayVal && Number.isFinite(startM) && Number.isFinite(endM) && endM > startM;

    const byRoom = new Map<
      string,
      { capacity: number; used: number; available: number; isFull: boolean; bookedDentists: string[] }
    >();

    for (const r of rooms) {
      const cap = Number.isFinite(Number(r.doctorsCount)) ? Number(r.doctorsCount) : 0;
      byRoom.set(r.roomNumber, {
        capacity: cap,
        used: 0,
        available: cap,
        isFull: false,
        bookedDentists: [],
      });
    }

    if (!canCompute) return byRoom;

    for (const existing of existingSessions) {
      if (editSource) {
        const sameRow =
          String(existing.doctorId ?? "").trim() ===
            String(editSource.doctorId ?? "").trim() &&
          String(existing.roomId ?? "").trim() ===
            String(editSource.roomId ?? "").trim() &&
          String(existing.day ?? "").trim() ===
            String(editSource.day ?? "").trim() &&
          String(existing.sessionName ?? "").trim() ===
            String(editSource.sessionName ?? "").trim();
        if (sameRow) continue;
      }
      if (String(existing.day ?? "").trim() !== dayVal) continue;
      const roomNo = String(existing.roomNo ?? "").trim();
      if (!roomNo) continue;
      const eStart = flexibleTimeToMinutes(existing.startTime);
      const eEnd = flexibleTimeToMinutes(existing.endTime);
      if (!Number.isFinite(eStart) || !Number.isFinite(eEnd)) continue;
      if (!rangesOverlap(startM, endM, eStart, eEnd)) continue;

      const st = byRoom.get(roomNo);
      if (!st) continue;
      st.used += 1;
      st.bookedDentists.push(String(existing.dentistName ?? "").trim());
    }

    for (const [roomNo, st] of Array.from(byRoom.entries())) {
      st.available = Math.max(0, st.capacity - st.used);
      st.isFull = st.capacity > 0 ? st.available <= 0 : st.used > 0;
      st.bookedDentists = Array.from(new Set(st.bookedDentists.filter(Boolean)));
      byRoom.set(roomNo, st);
    }

    return byRoom;
  }, [newRow.day, newRow.startTime, newRow.endTime, rooms, existingSessions, editSource]);

  const getRoomStatus = useCallback(
    (roomNumber: string) => currentRoomStatus.get(roomNumber) ?? null,
    [currentRoomStatus]
  );

  useEffect(() => {
    const roomNo = String(newRow.roomNo ?? "").trim();
    if (!roomNo) {
      setRoomCapacityError(null);
      return;
    }
    const st = getRoomStatus(roomNo);
    if (!st) {
      setRoomCapacityError(null);
      return;
    }
    if (st.isFull) {
      setRoomCapacityError(
        `Room ${roomNo} is full for the selected time (${st.used}/${st.capacity} doctors).`
      );
    } else if (st.capacity > 0 && (newRow.startTime || newRow.endTime)) {
      setRoomCapacityError(
        `Available doctors: ${st.available}/${st.capacity}${
          st.bookedDentists.length ? ` (Booked: ${st.bookedDentists.join(", ")})` : ""
        }`
      );
    } else {
      setRoomCapacityError(null);
    }
  }, [newRow.roomNo, newRow.day, newRow.startTime, newRow.endTime, getRoomStatus]);

  const sessionTemplate = useMemo(() => {
    const name = newRow.sessionName?.trim();
    if (!name) return null;
    return sessionNameOptionsForModal.find((s) => s.sessionName === name) ?? null;
  }, [sessionNameOptionsForModal, newRow.sessionName]);

  const startTimeOptions = useMemo(() => {
    const sm = timeToMinutes(sessionTemplate?.startTime ?? "");
    const em = timeToMinutes(sessionTemplate?.endTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      return Number.isFinite(m) && m >= sm && m < em;
    });
  }, [sessionTemplate]);

  const endTimeOptions = useMemo(() => {
    const sm = timeToMinutes(sessionTemplate?.startTime ?? "");
    const em = timeToMinutes(sessionTemplate?.endTime ?? "");
    const curStart = timeToMinutes(newRow.startTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      if (!Number.isFinite(m)) return false;
      if (Number.isFinite(curStart) && m <= curStart) return false;
      return m > sm && m <= em;
    });
  }, [sessionTemplate, newRow.startTime]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: DentistSessionFormRow = {
      dentistName: ensureDrPrefix(newRow.dentistName),
      day: (fixedDay || newRow.day).trim(),
      sessionName: newRow.sessionName.trim(),
      startTime: newRow.startTime.trim(),
      endTime: newRow.endTime.trim(),
      roomNo: newRow.roomNo.trim(),
      doctorId: String(newRow.doctorId ?? "").trim(),
      roomId: String(newRow.roomId ?? "").trim(),
    };
    if (
      !trimmed.dentistName ||
      !trimmed.sessionName ||
      !trimmed.day ||
      !trimmed.startTime ||
      !trimmed.endTime
    ) {
      return;
    }

    if (trimmed.roomNo) {
      const st = getRoomStatus(trimmed.roomNo);
      if (st?.isFull) {
        toast.error("Room is full for selected time.");
        return;
      }
    }

    const payload: Record<string, unknown> = {
      doctorId: trimmed.doctorId,
      day: trimmed.day,
      sessionName: trimmed.sessionName,
      startTime: trimmed.startTime,
      endTime: trimmed.endTime,
    };
    if (trimmed.roomId) {
      payload.roomId = trimmed.roomId;
    }
    if (editSource) {
      payload.oldSessionName = String(editSource.sessionName ?? "").trim();
      payload.oldRoomId = String(editSource.roomId ?? "").trim();
    }

    try {
      await baseUrl.post("/client/setting/dentistGeneralHours/create", payload);
      toast.success(editSource ? "Updated" : "Saved");
      await onSaved();
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not save"
      );
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div className="relative w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-visible">
        <form
          onSubmit={onSave}
          className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">
              {editSource ? "Edit Session" : "Create Session"}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div className="space-y-1 block">
              <div id="create-dentist-label" className="text-xs font-semibold text-slate-600">
                Dentist
              </div>
              <DentistCombobox
                id="create-dentist"
                labelId="create-dentist-label"
                value={newRow.dentistName}
                options={dentistOptions}
                loading={dentistsLoading}
                required
                disabled={!!editSource}
                onSelect={(d) =>
                  setNewRow((p) => ({
                    ...p,
                    dentistName: d.name,
                    doctorId: d._id,
                  }))
                }
              />
            </div>

            <label className="space-y-1 block">
              <div className="text-xs font-semibold text-slate-600">Day</div>
              {fixedDay ? (
                <div className="w-full px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm font-medium text-slate-800">
                  {fixedDay}
                </div>
              ) : (
                <div className="space-y-1">
                  <div id="create-day-label" className="sr-only">
                    Day
                  </div>
                  <SimpleCombobox
                    id="create-day"
                    labelId="create-day-label"
                    value={newRow.day}
                    options={weekDays}
                    placeholder="Search day…"
                    required
                    disabled={!!editSource}
                    onSelect={(d) =>
                      setNewRow((p) => ({
                        ...p,
                        day: d,
                        sessionName: "",
                        startTime: "",
                        endTime: "",
                      }))
                    }
                  />
                </div>
              )}
            </label>

            <label className="space-y-1 block">
              <div className="text-xs font-semibold text-slate-600">Session Name</div>
              <div id="create-session-label" className="sr-only">
                Session Name
              </div>
              <SessionCombobox
                id="create-session"
                labelId="create-session-label"
                value={newRow.sessionName}
                options={sessionNameOptionsForModal}
                loading={sessionNamesLoading}
                required
                onSelect={(s) =>
                  setNewRow((p) => ({
                    ...p,
                    sessionName: s.sessionName,
                    startTime: s.startTime ?? "",
                    endTime: s.endTime ?? "",
                  }))
                }
              />
              {!sessionNamesLoading && sessionNameOptionsForModal.length === 0 ? (
                <p className="text-xs text-slate-500">
                  No sessions for this day (create in General Open Hours)
                </p>
              ) : null}
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1">
                <div className="text-xs font-semibold text-slate-600">Start Time</div>
                <div id="create-start-time-label" className="sr-only">
                  Start Time
                </div>
                <SimpleCombobox
                  id="create-start-time"
                  labelId="create-start-time-label"
                  value={newRow.startTime}
                  options={startTimeOptions}
                  placeholder="Search time…"
                  required
                  onSelect={(t) =>
                    setNewRow((p) => ({
                      ...p,
                      startTime: t,
                      endTime:
                        p.endTime && timeToMinutes(p.endTime) > timeToMinutes(t)
                          ? p.endTime
                          : "",
                    }))
                  }
                />
              </label>

              <label className="space-y-1">
                <div className="text-xs font-semibold text-slate-600">End Time</div>
                <div id="create-end-time-label" className="sr-only">
                  End Time
                </div>
                <SimpleCombobox
                  id="create-end-time"
                  labelId="create-end-time-label"
                  value={newRow.endTime}
                  options={endTimeOptions}
                  placeholder="Search time…"
                  required
                  onSelect={(t) => setNewRow((p) => ({ ...p, endTime: t }))}
                />
              </label>
            </div>

            <label className="space-y-1 block">
              <div className="text-xs font-semibold text-slate-600">Room No</div>
              <div id="create-room-label" className="sr-only">
                Room No
              </div>
              <RoomCombobox
                id="create-room"
                labelId="create-room-label"
                value={newRow.roomNo}
                options={rooms}
                loading={roomsLoading}
                optional
                getStatus={getRoomStatus}
                onSelect={(r) =>
                  setNewRow((p) => ({
                    ...p,
                    roomNo: r.roomNumber,
                    roomId: r._id,
                  }))
                }
                onClear={() =>
                  setNewRow((p) => ({
                    ...p,
                    roomNo: "",
                    roomId: "",
                  }))
                }
              />
              <p className="text-xs text-slate-500">
                Optional — assign a room now or later in Room Allocation.
              </p>
              {roomCapacityError ? (
                <p
                  className={
                    getRoomStatus(newRow.roomNo)?.isFull
                      ? "text-xs text-red-600"
                      : "text-xs text-slate-500"
                  }
                >
                  {roomCapacityError}
                </p>
              ) : null}
            </label>
          </div>

          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
            >
              {editSource ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
